import { Injectable, inject } from '@angular/core';
import { AuthService, AuthStorageService } from '@umss/shared/data-access';
import { SideNavGroup, SideNavUser } from '@umss/shared/ui';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private auth = inject(AuthService);
  private authStorage = inject(AuthStorageService);

  /**
   * Get business navigation structure
   * Returns the side-nav groups configuration for the business app
   * Matches Figma design structure exactly
   */
  getBusinessNavigation(): SideNavGroup[] {
    return [
      {
        label: '',
        items: [
          {
            label: 'Dashboard',
            value: 'dashboard',
            iconName: 'phosphorHouse',
            selected: false
          },
          {
            label: 'Shift Payments',
            value: 'payments',
            iconName: 'phosphorInvoice',
            selected: false
          },
          {
            label: 'Subscription Payments',
            value: 'subscriptionPayments',
            iconName: 'phosphorReceipt',
            selected: false
          },
          {
            label: 'Professionals',
            value: 'contractors',
            iconName: 'phosphorUsers',
            selected: false
          },
          {
            label: 'Facilities',
            value: 'facilities',
            iconName: 'phosphorBuildings',
            selected: false
          },
          {
            label: 'Communications',
            value: 'communications',
            iconName: 'phosphorChats',
            selected: false
          },
          {
            label: 'Admins',
            value: 'admins',
            iconName: 'phosphorUsersThree',
            selected: false
          },
          {
            label: 'Pay Cycle Tools',
            value: 'payCycleTools',
            iconName: 'phosphorGear',
            selected: false
          },
          {
            label: 'Backfills',
            value: 'backfills',
            iconName: 'phosphorArrowsClockwise',
            selected: false
          },
          {
            label: 'User',
            value: 'user',
            iconName: '',
            selected: false
          },
          {
            label: 'Sign Out',
            value: 'signOut',
            iconName: 'phosphorSignOut',
            selected: false
          }
        ]
      }
    ];
  }

  /**
   * Get current user information for side-nav display
   * Integrates with AuthService to get real user data
   * Returns Observable to support async user data loading
   */
  getCurrentUser(): SideNavUser | undefined {
    const authState = this.authStorage.state;

    if (!authState.lastSessionAt || !authState.user) {
      return undefined;
    }

    const user = authState.user;

    return {
      name: `${user.firstName} ${user.lastName}`,
      role: this.formatRole(authState.role),
      avatarSrc: undefined, // Can be extended to fetch from user profile API
      initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    };
  }

  /**
   * Get current user as Observable
   * Useful for reactive updates when auth state changes
   */
  getCurrentUser$(): Observable<SideNavUser | undefined> {
    return this.authStorage.state$.pipe(
      map((authState) => {
        if (!authState.lastSessionAt || !authState.user) {
          return undefined;
        }

        const user = authState.user;

        return {
          name: `${user.firstName} ${user.lastName}`,
          role: this.formatRole(authState.role),
          avatarSrc: undefined,
          initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        };
      })
    );
  }

  /**
   * Get route path for a navigation item value
   * Maps nav item values to actual routes
   */
  getRouteForValue(value: string): string {
    const routeMap: Record<string, string> = {
      dashboard: '/dashboard',
      payments: '/payments',
      subscriptionPayments: '/payments/subscriptions',
      contractors: '/professionals',
      facilities: '/facilities',
      communications: '/communications',
      admins: '/admins',
      payCycleTools: '/pay-cycle-tools',
      backfills: '/backfills',
      settings: '/settings',
      user: '/profile',
      signOut: '/auth/login'
    };

    return routeMap[value] || '/dashboard';
  }

  /**
   * Update selected nav item based on current route
   * Useful for highlighting active navigation item
   */
  updateSelectedItem(
    currentRoute: string,
    groups: SideNavGroup[]
  ): SideNavGroup[] {
    const currentPath = currentRoute.split('?')[0];

    /**
     * The deepest route the current path sits on, or empty when it sits on
     * none.
     *
     * Longest match rather than equality, for two reasons. Shift Payments'
     * route is a prefix of Subscription Payments', so equality is the only
     * thing keeping `/payments/subscriptions` from selecting both — and it
     * does that by selecting neither, since the path equals neither route.
     * And every detail page below a section — an invoice, a facility profile
     * — used to leave the whole nav unselected for the same reason.
     */
    const selectedRoute = groups
      .flatMap((group) => group.items)
      .map((item) => this.getRouteForValue(item.value))
      .filter((route) => this.isOnRoute(currentPath, route))
      .reduce(
        (deepest, route) => (route.length > deepest.length ? route : deepest),
        ''
      );

    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        selected:
          selectedRoute !== '' &&
          this.getRouteForValue(item.value) === selectedRoute
      }))
    }));
  }

  /**
   * Whether a path sits on a nav route — the route itself, or anything below
   * it.
   *
   * Compared on the segment boundary rather than as a bare prefix, so a future
   * `/payments-history` is not read as being inside `/payments`.
   */
  private isOnRoute(path: string, route: string): boolean {
    return path === route || path.startsWith(`${route}/`);
  }

  /**
   * Update badge count for a specific nav item
   * Useful for notifications, pending items, etc.
   */
  updateBadgeCount(
    groups: SideNavGroup[],
    itemValue: string,
    count: number
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.value === itemValue) {
          return {
            ...item,
            badge: count,
            showBadge: count > 0
          };
        }
        return item;
      })
    }));
  }

  /**
   * Get navigation filtered by user role/permissions
   * Can be extended to hide nav items based on user permissions
   */
  getNavigationByRole(): SideNavGroup[] {
    const allNavigation = this.getBusinessNavigation();

    // For now, return all navigation
    // Can be extended to filter based on role:
    // if (authState.role === 'business') {
    //   return allNavigation; // Show all
    // } else if (authState.role === 'ShiftAdministrator') {
    //   return filterNavigation(allNavigation, ['dashboard', 'scheduling']);
    // }

    return allNavigation;
  }

  /**
   * Format user role for display
   * Converts role enum to human-readable format
   */
  private formatRole(role: string | null): string {
    if (!role) return 'User';

    // Convert camelCase/PascalCase to Title Case with spaces
    return role
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  }
}
